import { pricingHotelCompetitors } from '../../competitor-importance/PricingAutomationStrategyCompetitorsImportance.utilities';
import Drawer, { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import React from 'react';
import PricingStrategy from '@app/model/pricing-automation/PricingStrategy';
import PricingStrategyRuleEvaluate from '@app/model/pricing-automation/PricingStrategyRuleEvaluate';
import { useStore } from '@tanstack/react-form';
import PricingStrategyRuleType from '@app/model/pricing-automation/PricingStrategyRuleType';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import PricingStrategyInput from '@app/model/pricing-automation/input/PricingStrategyInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import useAppForm from '@app/site/_form';
import { z } from 'zod';
import PricingStrategyRuleInput from '@app/model/pricing-automation/input/PricingStrategyRuleInput';
import _isEmpty from 'lodash/isEmpty';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import { createRoomLimitPreview } from './PricingAutomationStrategyDrawer.utilities';
import PricingRoomName from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName';

type Props = {
	architecture: PricingArchitecture;
	strategy?: PricingStrategy
} & Omit<DrawerProps, 'title'>;

const EvaluateOptions = [
	{
		id: PricingStrategyRuleEvaluate.LEAST_EXPENSIVE,
		label: 'Least Expensive',
		value: PricingStrategyRuleEvaluate.LEAST_EXPENSIVE,
	},
	{
		id: PricingStrategyRuleEvaluate.MEDIAN,
		label: 'Median',
		value: PricingStrategyRuleEvaluate.MEDIAN,
	},
	{
		id: PricingStrategyRuleEvaluate.MOST_EXPENSIVE,
		label: 'Most Expensive',
		value: PricingStrategyRuleEvaluate.MOST_EXPENSIVE,
	},
];

type PricingRuleSectionProps = {
	form: any;
	title: string;
	prefix: 'minimum' | 'maximum';
	strategyLabel: string;
	amountLabel: string;
	evaluateLabel: string;
	percentageLabel: string;
	thresholdLabel: string;
	followsRoomAdjustmentsHelp: string;
	competitors: Array<{ id: number; name: string }>;
};

const normalizeOptionalNumber = (value: unknown): number | undefined => {
	if (value === null || value === undefined) return undefined;
	if (typeof value === 'number') return value;
	if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
	return undefined;
};

const normalizeOptionalNumberArray = (value: unknown): number[] | undefined => {
	if (value === null || value === undefined) return undefined;
	if (Array.isArray(value)) return value.filter((v): v is number => typeof v === 'number');
	return undefined;
};

const normalizeRuleForForm = (rule: PricingStrategyRuleInput | undefined): PricingStrategyRuleInput => {
	const type = rule?.type ?? PricingStrategyRuleType.STATIC;
	return {
		id: rule?.id,
		type,
		amount: normalizeOptionalNumber((rule as any)?.amount),
		percentage: normalizeOptionalNumber((rule as any)?.percentage),
		threshold: normalizeOptionalNumber((rule as any)?.threshold),
		evaluate: ((rule as any)?.evaluate ?? undefined) as any,
		competitorIds: normalizeOptionalNumberArray((rule as any)?.competitorIds),
		followsRoomAdjustments: rule?.followsRoomAdjustments ?? false,
	};
};


const PricingRuleSection: React.FunctionComponent<PricingRuleSectionProps> = ({
	form,
	title,
	prefix,
	strategyLabel,
	amountLabel,
	evaluateLabel,
	percentageLabel,
	thresholdLabel,
	followsRoomAdjustmentsHelp,
	competitors,
}) => {
	// @ts-ignore
	const type = useStore(form.store, (state) => state.values[prefix].type);

	// @ts-ignore
	return <div className="rounded-lg px-7 py-2">
		<h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">{title}</h3>

		<form.AppField name={`${prefix}.type` as any}>
			{(typeField: any) =>
				<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
					<div className="col-span-6">
						<p className="text-sm font-semibold leading-6 text-gray-900 mb-1">{strategyLabel}</p>
						<div className="sm:flex sm:flex-wrap sm:items-center sm:space-x-8 space-y-3 sm:space-y-0">
							<div className="flex items-center">
								<typeField.RadioField label="Static" value={PricingStrategyRuleType.STATIC} />
							</div>
							<div className="flex items-center">
								<typeField.RadioField label="Dynamic" value={PricingStrategyRuleType.DYNAMIC} />
							</div>
						</div>
					</div>
					<div className="col-span-3" hidden={type !== PricingStrategyRuleType.STATIC}>
						<form.AppField name={`${prefix}.amount` as any}>
							{/* @ts-ignore */}
							{field => <field.NumberField label={amountLabel} extraClassName="w-full" />}
						</form.AppField>
					</div>
					<div className="col-span-6" hidden={type !== PricingStrategyRuleType.DYNAMIC}>
						<form.AppField name={`${prefix}.competitorIds` as any}>
							{/* @ts-ignore */}
							{field => <div>
								<field.MultiSelectField label="Select competitors" extraClassName="w-full"
									placeholder="Select competitors" optionsSize="xxlarge"
									options={competitors.map(c => ({ label: c.name, value: c.id, id: String(c.id) }))} />
								{field.state.value?.some((id: number) => !competitors.some(c => c.id === id)) === true &&
									<p className="mt-1 text-xs text-red-600">
										This rule includes competitors that are no longer eligible for pricing.{' '}
										<button type="button" className="underline" onClick={() => field.handleChange(
											field.state.value.filter((id: number) => competitors.some(c => c.id === id)))}>
											Remove unavailable selections
										</button>
									</p>}
							</div>}
						</form.AppField>
					</div>
					<div className="col-span-3" hidden={type !== PricingStrategyRuleType.DYNAMIC}>
						<form.AppField name={`${prefix}.evaluate` as any}>
							{/* @ts-ignore */}
							{field => <field.SelectField label={evaluateLabel} placeholder="Select strategy"
							                             extraClassName="w-full" options={EvaluateOptions} />}
						</form.AppField>
					</div>
					<div className="col-span-3" hidden={type !== PricingStrategyRuleType.DYNAMIC}>
						<form.AppField name={`${prefix}.percentage` as any}>
							{/* @ts-ignore */}
							{field => <field.NumberField label={percentageLabel} extraClassName="w-full" />}
						</form.AppField>
					</div>
					<div className="col-span-6" hidden={type !== PricingStrategyRuleType.DYNAMIC}>
						<form.AppField name={`${prefix}.threshold` as any}>
							{/* @ts-ignore */}
							{field => <field.NumberField label={thresholdLabel} extraClassName="w-full" />}
						</form.AppField>
					</div>
					<div className="col-span-6 flex items-start justify-between gap-6">
						<div>
							<p className="text-sm font-semibold text-gray-900">Follow room adjustments</p>
							<p className="mt-1 text-sm text-gray-500">{followsRoomAdjustmentsHelp}</p>
						</div>
						<form.AppField name={`${prefix}.followsRoomAdjustments` as any}>
							{(field: any) => <field.SwitchField label="Follow room adjustments" isLarge />}
						</form.AppField>
					</div>
					{/* @ts-ignore */}
					<form.Subscribe selector={(state) => state.values[prefix]}>
						{/* @ts-ignore */}
						{(dynamicRule) => <RenderDynamicStrategyDescription rule={dynamicRule} prefix={prefix} />}
					</form.Subscribe>
				</div>}
		</form.AppField>
	</div>;
};

const RenderDynamicStrategyDescription: React.FunctionComponent<{
	rule: PricingStrategyRuleInput,
	prefix: string
}> = ({ rule, prefix }) => {
	if (_isEmpty(rule.competitorIds) || !rule.evaluate || rule.percentage === undefined || rule.threshold === undefined || rule.type === PricingStrategyRuleType.STATIC) return null;
	return <div className="col-span-6">
		<div className="w-full rounded-lg bg-white ring-1 ring-black ring-opacity-10">
			<div className="p-4">
				<div className="flex items-start">
					<div className="flex-shrink-0">
						<CheckCircleIcon className="h-6 w-6 text-green-400" />
					</div>
					<div className="ml-3 w-0 flex-1 pt-0.5">
						<p className="text-sm font-medium text-gray-900">
							Your {prefix} price strategy translates to:
						</p>
						<p
							className="mt-1 text-sm text-gray-500">
							{EvaluateOptions.find(o => o.id === rule.evaluate)?.label} {rule.percentage}% of
							the {prefix === 'minimum' ? 'lowest' : 'highest'} competitor price with a threshold of {rule.threshold}
							{rule.followsRoomAdjustments && " · Limit follows each room's adjustment"}
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>;
};

const PricingAutomationStrategyDrawer = React.memo(
	function PricingAutomationStrategyDrawer({ architecture, strategy, ...props }: Props) {
		const title = strategy ? 'Edit Pricing Strategy' : 'Create Pricing Strategy';

		const { companyHotel } = useSelectedCompanyHotel();
		const toaster = useToaster();
		const { t } = useTranslation();

		const PricingStrategyDefaultValues: PricingStrategyInput = {
			companyHotelId: companyHotel.id,
			architectureId: strategy?.architectureId || architecture.id,
			minimum: normalizeRuleForForm(strategy?.minimum),
			maximum: normalizeRuleForForm(strategy?.maximum),
		};

		const PricingStrategyRuleSchema = z.discriminatedUnion('type', [
			z.object({
				id: z.number().optional(),
				type: z.literal(PricingStrategyRuleType.STATIC),
				amount: z.number().optional().refine((val) => val !== undefined && val >= 1, {
					message: 'Amount is required',
				}),
				percentage: z.number().optional(),
				threshold: z.number().optional(),
				evaluate: z.nativeEnum(PricingStrategyRuleEvaluate).optional(),
				competitorIds: z.array(z.number()).optional(),
				followsRoomAdjustments: z.boolean(),
			}),
			z.object({
				id: z.number().optional(),
				type: z.literal(PricingStrategyRuleType.DYNAMIC),
				percentage: z.number().optional().refine((val) => val !== undefined && val >= -100 && val <= 100, {
					message: 'Percentage is required',
				}),
				threshold: z.number().optional().refine((val) => val !== undefined && val >= 1, {
					message: 'Threshold is required',
				}),
				evaluate: z.nativeEnum(PricingStrategyRuleEvaluate).optional().refine((val) => val !== undefined, {
					message: 'Evaluation is required',
				}),
				competitorIds: z.array(z.number()).optional().refine((val) => val !== undefined && val.length > 0 && val.every(id => pricingHotelCompetitors(companyHotel.competitors).some(c => c.id === id)), {
					message: 'Select at least one eligible hotel competitor',
				}),
				amount: z.number().optional(),
				followsRoomAdjustments: z.boolean(),
			}),
		]);

		const PricingStrategySchema = z.object({
			id: z.number().optional(),
			companyHotelId: z.number().min(1, 'Company Hotel is required'),
			architectureId: z.number().min(1, 'Architecture is required'),
			minimum: PricingStrategyRuleSchema,
			maximum: PricingStrategyRuleSchema,
		});

		const queryClient = useQueryClient();

		const savePricingStrategyMutation = useMutation({
			mutationFn: PricingAutomationService.savePricingStrategy,
			onSuccess: (architecture: PricingArchitecture) => {
				const queryKey = PricingAutomationQueryKeys.pricingArchitectures(companyHotel.id);
				queryClient.setQueryData<PricingArchitecture[]>(queryKey, (cachedArchitectures = []) => {
					const architectureExists = cachedArchitectures.some(cachedArchitecture => cachedArchitecture.id === architecture.id);

					if (!architectureExists)
						return [...cachedArchitectures, architecture];

					return cachedArchitectures.map(cachedArchitecture =>
						cachedArchitecture.id === architecture.id ? architecture : cachedArchitecture,
					);
				});

				toaster.success('Pricing Strategy', 'Pricing strategy saved successfully');
				props.ref!.current!.close();
			},
		});

		const form = useAppForm({
			defaultValues: PricingStrategyDefaultValues,
			validators: {
				onChange: PricingStrategySchema,
			},
			onSubmit: ({ value }) => savePricingStrategyMutation.mutateAsync(value).catch((error) => {
				toaster.error('Pricing Strategy', t(error));
			}),
		});

		const renderRoomLimitPreview = (minimum: PricingStrategyRuleInput, maximum: PricingStrategyRuleInput) => {
			const rows = createRoomLimitPreview(architecture, minimum, maximum);
			const formatLimit = (value: number | undefined) => value === undefined ? '—' : Math.round(value).toString();

			return <section className="mx-7 mb-7 rounded-lg border border-gray-200 bg-white">
				<div className="border-b border-gray-200 px-4 py-3">
					<h3 className="text-sm font-semibold text-gray-900">Market price limits</h3>
					<p className="mt-1 text-xs text-gray-500">Limits are shown on the market price scale and converted to WebHotelier prices for each master occupancy. Dynamic previews use the manual threshold; the resolved market limit may differ.</p>
				</div>
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200 text-sm">
						<thead className="bg-gray-50 text-left text-xs font-semibold text-gray-600">
						<tr><th className="px-4 py-2">Room</th><th className="px-4 py-2">Adjustment</th><th className="px-4 py-2">Min limit</th><th className="px-4 py-2">Max limit</th></tr>
						</thead>
						<tbody className="divide-y divide-gray-100">
						{rows.map(row => <tr key={row.roomId}>
							<td className="px-4 py-2 font-medium text-gray-900"><PricingRoomName room={row} /></td>
							<td className="px-4 py-2 text-gray-600">{row.adjustment}</td>
							<td className="px-4 py-2 text-gray-600">{formatLimit(row.minimum)}</td>
							<td className="px-4 py-2 text-gray-600">{formatLimit(row.maximum)}</td>
						</tr>)}
						</tbody>
					</table>
				</div>
				{(!minimum.followsRoomAdjustments || !maximum.followsRoomAdjustments) && <div className="space-y-1 border-t border-gray-200 px-4 py-3 text-xs text-gray-500">
					{!minimum.followsRoomAdjustments && <p>Minimum is shared by all rooms and can override a room adjustment.</p>}
					{!maximum.followsRoomAdjustments && <p>Maximum is shared by all rooms and can override a room adjustment.</p>}
				</div>}
			</section>;
		};


		return <form.AppForm>
			<form onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
			}}>
				<Drawer {...props} title={title} size="large" actions={
					<>
						<form.ResetButton state="text">
							Reset
						</form.ResetButton>
						<form.SubmitButton state="primary">
							Save
						</form.SubmitButton>
					</>
				}>
					<div className="pt-6 border-t border-gray-200">
						<div className="text-sm text-pretty p-7">
							<p className="tracking-tight text-gray-900">Configure and personalize your automation in the form provided
								below.</p>
						</div>

						<PricingRuleSection
							form={form}
							title="Minimum Price"
							prefix="minimum"
							strategyLabel="Select your minimum price strategy"
							amountLabel="Set minimum price"
							evaluateLabel="Set min strategy"
							percentageLabel="Minimum change percentage (%)"
							thresholdLabel="Set manual threshold (lowest minimum price)"
							followsRoomAdjustmentsHelp="On: adjust the market minimum for each derivative room. Off: all rooms share the same market minimum, which can override a room adjustment."
							competitors={pricingHotelCompetitors(companyHotel.competitors)}
						/>

						<PricingRuleSection
							form={form}
							title="Maximum Price"
							prefix="maximum"
							strategyLabel="Select your maximum price strategy"
							amountLabel="Set maximum price"
							evaluateLabel="Set max strategy"
							percentageLabel="Maximum change percentage (%)"
							thresholdLabel="Set manual threshold (highest maximum price)"
							followsRoomAdjustmentsHelp="On: adjust the market maximum for each derivative room. Off: all rooms share the same market maximum, which can override a room adjustment."
							competitors={pricingHotelCompetitors(companyHotel.competitors)}
						/>

						<form.Subscribe selector={(state) => [state.values.minimum, state.values.maximum] as const}>
							{([minimum, maximum]) => renderRoomLimitPreview(minimum, maximum)}
						</form.Subscribe>

						{/*<FormValidationDebug form={form} />*/}
					</div>
				</Drawer>
			</form>
		</form.AppForm>;

	},
);

export default PricingAutomationStrategyDrawer;
